import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LoanOfficer, computeReviewStatus, getWeekReview } from '../../data/mockData';
import { useData } from '../../context/DataContext';
import { Card, CardContent } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { ProgressRing } from '../ui/ProgressRing';
import { ChevronDown, ChevronUp, Calendar, Target } from 'lucide-react';
import { LODetails } from './LODetails';
interface LOCardProps {
  lo: LoanOfficer;
}
export function LOCard({
  lo
}: LOCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    selectedWeek
  } = useData();
  const reviewStatus = computeReviewStatus(getWeekReview(lo, selectedWeek));
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'complete':
        return <Badge variant="success" data-id="element-161">Complete</Badge>;
      case 'in_progress':
        return <Badge variant="warning" data-id="element-162">In Progress</Badge>;
      default:
        return <Badge variant="neutral" data-id="element-163">Not Started</Badge>;
    }
  };
  return <motion.div layout className="mb-4" data-id="element-164">
      <Card className={`border-slate-200 transition-shadow hover:shadow-md ${isExpanded ? 'ring-1 ring-slate-900' : ''}`} data-id="element-165">
        <CardContent className="p-0" data-id="element-166">
          <div className="p-5 flex flex-col md:flex-row items-start md:items-center justify-between cursor-pointer" onClick={() => setIsExpanded(!isExpanded)} data-id="element-167">
            {/* LO Info */}
            <div className="flex items-center space-x-4 w-full md:w-1/3 mb-4 md:mb-0" data-id="element-168">
              <img src={lo.avatar} alt={lo.name} className="w-12 h-12 rounded-full border-2 border-slate-100" data-id="element-169" />
              <div data-id="element-170">
                <h3 className="text-lg font-bold text-slate-900" data-id="element-171">{lo.name}</h3>
                <p className="text-sm text-slate-500" data-id="element-172">{lo.team}</p>
              </div>
            </div>

            {/* Production Quick Stats */}
            <div className="flex items-center space-x-6 w-full md:w-1/3 mb-4 md:mb-0" data-id="element-173">
              <div className="text-center" data-id="element-174">
                <span className="block text-xs text-slate-500 uppercase" data-id="element-175">
                  Apps
                </span>
                <span className="block font-semibold text-slate-900" data-id="element-176">
                  {lo.production.applications}
                </span>
              </div>
              <div className="text-center" data-id="element-177">
                <span className="block text-xs text-slate-500 uppercase" data-id="element-178">
                  Funded
                </span>
                <span className="block font-semibold text-slate-900" data-id="element-179">
                  {lo.production.closings}
                </span>
              </div>
              <div className="text-center" data-id="element-180">
                <span className="block text-xs text-slate-500 uppercase" data-id="element-181">
                  Conv %
                </span>
                <span className="block font-semibold text-slate-900" data-id="element-182">
                  {lo.production.conversionRate}%
                </span>
              </div>
            </div>

            {/* Review Status & Score */}
            <div className="flex items-center justify-between md:justify-end space-x-6 w-full md:w-1/3" data-id="element-183">
              <div className="flex flex-col items-end" data-id="element-184">
                {getStatusBadge(reviewStatus)}
                <div className="flex items-center mt-1 text-xs text-slate-500" data-id="element-185">
                  <Calendar className="w-3 h-3 mr-1" data-id="element-186" />
                  {lo.lastReviewDate}
                </div>
              </div>
              <div className="flex items-center space-x-4" data-id="element-187">
                <ProgressRing value={lo.accountabilityScore} size={48} strokeWidth={4} colorClass={lo.accountabilityScore >= 80 ? 'text-emerald-500' : lo.accountabilityScore >= 60 ? 'text-amber-500' : 'text-rose-500'} data-id="element-188" />
                <button className="p-2 hover:bg-slate-100 rounded-full transition-colors" data-id="element-189">
                  {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-600" data-id="element-190" /> : <ChevronDown className="w-5 h-5 text-slate-600" data-id="element-191" />}
                </button>
              </div>
            </div>
          </div>

          <AnimatePresence data-id="element-192">
            {isExpanded && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} transition={{
            duration: 0.3,
            ease: 'easeInOut'
          }} className="overflow-hidden px-5 pb-5" data-id="element-193">
                <LODetails lo={lo} onComplete={() => setIsExpanded(false)} data-id="element-194" />
              </motion.div>}
          </AnimatePresence>
        </CardContent>
      </Card>
    </motion.div>;
}